// «Генерации для креативов» в настройках: потолок на ролик, дорогие модели, дневной потолок и расходы fal.ai
// и Gemini за 30 дней. Меняет только владелец; агент видит эти правила через шлюз генераций.
// Модели — словами («Озвучка», «Дорогая модель: видео из картинки»), имена моделей — только в технических деталях.
import { Card, Chip, toast, usd, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';

interface GenView {
  settings: { capPerCreative: number; approvedModels: string[]; dailyCapUsd: number | null };
  models: Array<{ model: string; label: string; kind: string; needsApproval: boolean }>;
  spend: Array<{ day: string; provider: string; usd: number }>;
  recent: Array<{
    id: string;
    kind: string;
    model: string;
    label: string | null;
    status: string;
    estUsd: number;
    costUsd: number | null;
    error: string | null;
    createdAt: string;
  }>;
}

const JOB_ST: Record<string, { label: string; tone: 'default' | 'info' | 'success' | 'danger' | 'accent' }> =
  {
    pending_approval: { label: 'ждёт вас', tone: 'accent' },
    queued: { label: 'в очереди', tone: 'info' },
    running: { label: 'в работе', tone: 'info' },
    done: { label: 'готово', tone: 'success' },
    failed: { label: 'ошибка', tone: 'danger' },
    cancelled: { label: 'отменено', tone: 'default' },
  };

/** Что делает генерация — словами, без имени модели. */
const KIND_RU: Record<string, string> = {
  image: 'Картинка',
  video: 'Видео из картинки',
  tts: 'Озвучка',
  judge: 'Оценка ролика',
  transcribe: 'Расшифровка',
  describe: 'Описание футажа',
};

/** «Kling 2.5 Turbo Pro (видео из картинки)» → имя модели и что она делает. */
function splitLabel(label: string | null, model: string): { tech: string; what: string | null } {
  const m = /^(.*?)\s*\((.+)\)\s*$/.exec(label ?? '');
  if (!m) return { tech: label ?? model, what: null };
  const what = m[2] ?? '';
  return { tech: m[1] || model, what: what.charAt(0).toUpperCase() + what.slice(1) };
}

const humanModel = (m: { label: string; model: string; kind: string; needsApproval: boolean }) => {
  const what = KIND_RU[m.kind] ?? splitLabel(m.label, m.model).what ?? 'Генерация';
  return m.needsApproval ? `Дорогая модель: ${what.toLowerCase()}` : what;
};

function Money({
  value,
  onSave,
  label,
}: {
  value: number | null;
  onSave: (n: number | null) => void;
  label: string;
}) {
  const [v, setV] = useState(value === null ? '' : String(value));
  useEffect(() => setV(value === null ? '' : String(value)), [value]);
  const commit = () => {
    const t = v.trim().replace(',', '.');
    if (!t) return value !== null && onSave(null);
    const n = Number(t);
    if (!Number.isFinite(n) || n <= 0) return setV(value === null ? '' : String(value));
    if (n !== value) onSave(n);
  };
  return (
    <input
      className="input guard-num"
      style={{ width: '7ch' }}
      inputMode="decimal"
      aria-label={label}
      value={v}
      placeholder="нет"
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

export function GenCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['gen'], queryFn: () => api<GenView>('/api/creatives/gen') });
  const s = q.data?.settings;
  const save = async (patch: Partial<GenView['settings']>) => {
    if (!s) return;
    const next = { ...s, ...patch };
    qc.setQueryData<GenView>(['gen'], (old) => (old ? { ...old, settings: next } : old));
    try {
      await api('/api/creatives/gen', next, 'PUT');
      toast('Сохранено');
    } catch (e) {
      toast((e as Error).message);
      void q.refetch();
    }
  };
  const total = (q.data?.spend ?? []).reduce((a, r) => a + r.usd, 0);
  const byProvider = (p: string) =>
    (q.data?.spend ?? []).filter((r) => r.provider === p).reduce((a, r) => a + r.usd, 0);
  const pricey = (q.data?.models ?? []).filter((m) => m.needsApproval);

  return (
    <Card title="Генерации для креативов">
      <p className="muted" style={{ margin: '0 0 12px', fontSize: 'var(--fs-sm)' }}>
        Картинки, видео, озвучка и оценка роликов идут только через платформу: ключи у неё, расход — в учёте,
        бюджет резервируется на батч. Повторный такой же запрос не оплачивается.
      </p>
      {!s ? (
        <p className="muted">Загружаю…</p>
      ) : (
        <ul className="guard-list">
          <li className="guard-row">
            <span />
            <div className="min-w-0">
              <b>Потолок на ролик</b>
              <p>
                Не больше{' '}
                <Money
                  value={s.capPerCreative}
                  label="Потолок на ролик, $"
                  onSave={(n) => n !== null && save({ capPerCreative: n })}
                />{' '}
                $ на один ролик батча. Цель — вдвое меньше.
              </p>
            </div>
          </li>
          {pricey.map((m) => (
            <li className="guard-row" key={m.model}>
              <input
                id={`gen-${m.model}`}
                type="checkbox"
                checked={s.approvedModels.includes(m.model)}
                onChange={(e) =>
                  save({
                    approvedModels: e.target.checked
                      ? [...s.approvedModels, m.model]
                      : s.approvedModels.filter((x) => x !== m.model),
                  })
                }
              />
              <div className="min-w-0">
                <label htmlFor={`gen-${m.model}`}>
                  <b>{humanModel(m)} — без вопроса</b>
                </label>
                <p>Выключено — каждая такая генерация ждёт вашего одобрения в «Решениях».</p>
              </div>
            </li>
          ))}
          <li className="guard-row">
            <span />
            <div className="min-w-0">
              <b>Дневной потолок сверх ваших батчей</b>
              <p>
                На генерации, которые вы не одобряли (агент зарезервировал бюджет сам или батч запустил режим
                агента), — не больше{' '}
                <Money
                  value={s.dailyCapUsd}
                  label="Дневной потолок сверх ваших батчей, $"
                  onSave={(n) => save({ dailyCapUsd: n })}
                />{' '}
                $ в день, дальше — с вашего одобрения. Одобренные вами батчи держит их бюджет, этот потолок их
                не останавливает. Пусто — без потолка.
              </p>
            </div>
          </li>
        </ul>
      )}
      <h3 className="guard-h">За 30 дней</h3>
      <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
        {total > 0
          ? `${usd(total)} — картинки и видео ${usd(byProvider('fal'))}, озвучка и оценка роликов ${usd(byProvider('gemini'))}`
          : 'Генераций ещё не было.'}
      </p>
      {q.data?.recent.length ? (
        <ul className="guard-list" style={{ marginTop: 8 }}>
          {q.data.recent.slice(0, 6).map((j) => {
            const st = JOB_ST[j.status] ?? { label: j.status, tone: 'default' as const };
            return (
              <li key={j.id} className="guard-row">
                <span />
                <div className="min-w-0">
                  <b title={splitLabel(j.label, j.model).tech}>
                    {KIND_RU[j.kind] ?? splitLabel(j.label, j.model).what ?? 'Генерация'}
                  </b>
                  <p>
                    <Chip tone={st.tone}>{st.label}</Chip> {usd(j.costUsd ?? j.estUsd)} · {when(j.createdAt)}
                    {j.error ? ` · ${j.error}` : ''}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
      {q.data?.models.length ? (
        <details className="sp-raw gen-tech">
          <summary>Технические детали</summary>
          <ul>
            {q.data.models.map((m) => (
              <li key={m.model}>
                {humanModel(m)} — {splitLabel(m.label, m.model).tech}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </Card>
  );
}
